import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Archive, ChevronDown, ChevronUp, FileText, RefreshCw, RotateCcw, CheckCircle2 } from 'lucide-react';
import { listAppHandoffNotes, listAppHandoffPosts, archiveMailboxItem, unarchiveMailboxItem } from '../api';
import { useMailboxUnread } from '../hooks/useMailboxUnread';

type EntrySource = 'provisioning' | 'cursor-session';
type MailboxStatus =
  | 'blocked'
  | 'addressed'
  | 'partial'
  | 'already-fixed'
  | 'external'
  | 'provisioning'
  | 'needs-review';

type MailboxEntry = {
  id: string;
  backendId: string;
  source: EntrySource;
  archiveSource: 'post' | 'note';
  timestamp: string | Date;
  message: string;
  tags: string[];
  author_key_id?: string | null;
  title?: string | null;
  unread: boolean;
  status: MailboxStatus;
  statusLabel: string;
  preview: string;
  archived_at?: string | Date | null;
};

const TWO_LINE_CLAMP_STYLE: React.CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
};

function toMs(ts: string | Date | null | undefined): number {
  if (!ts) return 0;
  if (ts instanceof Date) return ts.getTime();
  const d = new Date(String(ts));
  return Number.isFinite(d.getTime()) ? d.getTime() : 0;
}

function clampMessage(message: string, maxChars: number) {
  const s = String(message || '');
  if (s.length <= maxChars) return s;
  return `${s.slice(0, maxChars)}…`;
}

function cleanMarkdownLine(line: string): string {
  return String(line || '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s*/g, '')
    .replace(/^\s*[-*+]\s+/g, '')
    .replace(/^\s*\d+\.\s+/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_{1,2}([^_]+)_{1,2}/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractMeaningfulLines(message: string, title?: string | null): string[] {
  const ignoreRe =
    /^(summary|status|current status|response|devcockpit response|files changed|verification|verification result|tool_name|tool name|author_key_id|author key id|tags|notes?)[:\s]*$/i;
  const titleNorm = cleanMarkdownLine(title || '').toLowerCase();
  return String(message || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .split(/\r?\n+/)
    .map(cleanMarkdownLine)
    .filter(Boolean)
    .filter((line) => !ignoreRe.test(line))
    .filter((line) => line.toLowerCase() !== titleNorm)
    .filter((line) => line.length > 4);
}

function extractSentences(message: string): string[] {
  const flattened = cleanMarkdownLine(String(message || '').replace(/\r?\n+/g, ' '));
  return flattened
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function explicitStatusFromTags(tags: string[]): MailboxStatus | null {
  const tagSet = new Set((tags || []).map((t) => String(t || '').trim().toLowerCase()).filter(Boolean));
  if (tagSet.has('already-addressed') || tagSet.has('already-fixed')) return 'already-fixed';
  if (tagSet.has('addressed') || tagSet.has('resolved')) return 'addressed';
  if (tagSet.has('partial') || tagSet.has('partially-fixed')) return 'partial';
  if (tagSet.has('blocked') || tagSet.has('blocker')) return 'blocked';
  if (tagSet.has('external') || tagSet.has('stale')) return 'external';
  return null;
}

function explicitStatusFromMessage(message: string, title?: string | null): MailboxStatus | null {
  const lines = [title || '', ...String(message || '').split(/\r?\n+/)]
    .map(cleanMarkdownLine)
    .filter(Boolean)
    .slice(0, 12);
  for (const line of lines) {
    const normalized = line.toLowerCase();
    const statusMatch = normalized.match(/^(?:status|current status|mailbox status)\s*:\s*(.+)$/i);
    const value = statusMatch ? statusMatch[1].trim() : normalized;
    if (/^(already fixed|already addressed|already resolved)\b/.test(value)) return 'already-fixed';
    if (/^(addressed|resolved)\b/.test(value)) return 'addressed';
    if (/^(partial|partially fixed|partial fix)\b/.test(value)) return 'partial';
    if (/^(blocked|blocker)\b/.test(value)) return 'blocked';
    if (/^(external|stale|external dependency)\b/.test(value)) return 'external';
    if (/^(needs review|open|unresolved|pending)\b/.test(value)) return 'needs-review';
  }
  return null;
}

function inferStatus(source: EntrySource, tags: string[], message: string, title?: string | null): MailboxStatus {
  const fromTags = explicitStatusFromTags(tags);
  if (fromTags) return fromTags;
  const fromMessage = explicitStatusFromMessage(message, title);
  if (fromMessage) return fromMessage;
  if (source === 'provisioning') return 'provisioning';
  return 'needs-review';
}

function getStatusLabel(status: MailboxStatus): string {
  switch (status) {
    case 'blocked': return 'Blocked';
    case 'addressed': return 'Addressed';
    case 'partial': return 'Partial';
    case 'already-fixed': return 'Already fixed';
    case 'external': return 'External / stale';
    case 'provisioning': return 'Provisioning';
    default: return 'Needs review';
  }
}

function getStatusBadgeClassName(status: MailboxStatus): string {
  switch (status) {
    case 'blocked': return 'border-rose-200 bg-rose-100 text-rose-800';
    case 'addressed': return 'border-emerald-200 bg-emerald-100 text-emerald-800';
    case 'partial': return 'border-amber-200 bg-amber-100 text-amber-800';
    case 'already-fixed': return 'border-blue-200 bg-blue-100 text-blue-800';
    case 'external': return 'border-slate-200 bg-slate-100 text-slate-700';
    case 'provisioning': return 'border-violet-200 bg-violet-100 text-violet-800';
    default: return 'border-rose-200 bg-rose-100 text-rose-800';
  }
}

function buildPreview(source: EntrySource, status: MailboxStatus, message: string, title?: string | null): string {
  const lines = extractMeaningfulLines(message, title);
  const sentences = extractSentences(message);
  const response =
    lines[0] ||
    sentences[0] ||
    (source === 'provisioning'
      ? 'DevCockpit posted a provisioning handoff note for this app.'
      : 'DevCockpit posted a mailbox response for this app.');
  const summary =
    lines.find((line, idx) => idx > 0 && line !== response) ||
    sentences.find((sentence) => sentence !== response) ||
    (status === 'addressed' ? 'The note says this item has been dealt with.'
      : status === 'partial' ? 'The note says some work is done, but follow-up is still needed.'
      : status === 'already-fixed' ? 'The note says this was already fixed before this update.'
      : status === 'blocked' ? 'The note says this is blocked and still needs platform work.'
      : status === 'external' ? 'The note says this depends on an external or stale issue.'
      : status === 'provisioning' ? 'This entry records the app provisioning handoff state.'
      : 'Open the note to review the full handoff details.');
  return clampMessage(`${response} ${summary}`.trim(), 240);
}

function formatRelativeTime(ts: string | Date | null | undefined): string {
  const ms = toMs(ts);
  if (!ms) return 'unknown_time';
  const deltaMs = Date.now() - ms;
  if (deltaMs < 0) return 'just now';
  const sec = Math.floor(deltaMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(ms).toLocaleDateString();
}

function MailboxItem({
  entry,
  expanded,
  onToggleExpand,
  onArchive,
  onRestore,
  archiving,
}: {
  entry: MailboxEntry;
  expanded: boolean;
  onToggleExpand: () => void;
  onArchive: () => void;
  onRestore: () => void;
  archiving: boolean;
}) {
  const isArchived = Boolean(entry.archived_at);
  const messagePreview = clampMessage(entry.message, expanded ? 10_000 : 500);
  const timeStr = formatRelativeTime(entry.timestamp);

  return (
    <div
      className={[
        'rounded-lg border p-3 bg-white transition-all duration-200',
        isArchived ? 'border-slate-100 opacity-70' : entry.unread ? 'border-amber-200' : 'border-gray-200',
        'relative',
      ].join(' ')}
    >
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
          isArchived ? 'bg-emerald-400' : entry.unread ? 'bg-amber-500' : 'bg-transparent'
        }`}
      />

      <div className="flex items-start justify-between gap-3 ml-1">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={entry.source === 'cursor-session' ? 'default' : 'secondary'}>
              {entry.source === 'cursor-session' ? 'Cursor session' : 'Provisioning'}
            </Badge>
            <Badge variant="outline" className={getStatusBadgeClassName(entry.status)}>
              {entry.statusLabel}
            </Badge>
            {isArchived && (
              <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                Resolved
              </Badge>
            )}
            <span className="text-[11px] text-gray-500 font-mono">{timeStr}</span>
          </div>

          {entry.title && (
            <div className="mt-1 text-xs font-semibold text-gray-900 break-words">{entry.title}</div>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {isArchived ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onRestore}
              disabled={archiving}
              title="Restore to inbox"
              className="text-slate-500 hover:text-blue-700"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={onArchive}
              disabled={archiving}
              title="Mark as resolved"
              className="text-slate-400 hover:text-emerald-700"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onToggleExpand}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      <div className="mt-3 ml-1">
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            DevCockpit response
          </div>
          <p className="mt-1 text-sm text-slate-900 leading-5" style={expanded ? undefined : TWO_LINE_CLAMP_STYLE}>
            {entry.preview}
          </p>
        </div>
      </div>

      {expanded && entry.tags.length > 0 && (
        <div className="mt-3 ml-1 flex flex-wrap gap-1">
          {entry.tags.slice(0, 8).map((t, idx) => (
            <Badge key={`${t}-${idx}`} variant="outline">{t}</Badge>
          ))}
        </div>
      )}

      {expanded && (
        <div className="mt-3 ml-1">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
            Full handoff note
          </div>
          <pre className="bg-white border rounded p-3 text-[11px] font-mono whitespace-pre-wrap max-h-80 overflow-y-auto">
            {messagePreview}
          </pre>
        </div>
      )}

      {expanded && entry.author_key_id && (
        <div className="mt-2 ml-1 text-[11px] text-gray-500">Author: {entry.author_key_id}</div>
      )}
    </div>
  );
}

export function MailboxPanel({ appId }: { appId: string }) {
  const queryClient = useQueryClient();
  const { unreadCount, markAllRead, lastReadAt } = useMailboxUnread(appId);
  const lastReadMs = toMs(lastReadAt);

  const postsQ = useQuery({
    queryKey: ['apps', appId, 'handoff-posts'],
    queryFn: () => listAppHandoffPosts(appId, { limit: 20 }),
    staleTime: 10_000,
    refetchInterval: 30_000,
    enabled: Boolean(appId),
  });

  const notesQ = useQuery({
    queryKey: ['apps', appId, 'handoff-notes'],
    queryFn: () => listAppHandoffNotes(appId, { limit: 30 }),
    staleTime: 10_000,
    refetchInterval: 30_000,
    enabled: Boolean(appId),
  });

  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const [archivingIds, setArchivingIds] = useState<Set<string>>(() => new Set());
  const [showResolved, setShowResolved] = useState(false);

  const entries = useMemo(() => {
    const posts = Array.isArray(postsQ.data?.posts) ? postsQ.data!.posts : [];
    const notes = Array.isArray(notesQ.data?.notes) ? notesQ.data!.notes : [];

    const postEntries: MailboxEntry[] = posts.map((p: any, idx: number) => {
      const content = typeof p?.attributes?.content === 'string' ? p.attributes.content : '';
      const createdAt = p?.created_at;
      const unread = toMs(createdAt) > lastReadMs;
      const tags = Array.isArray(p?.attributes?.tags) ? p.attributes.tags.map(String) : [];
      const title = p?.attributes?.title || null;
      const status = inferStatus('provisioning', tags, content, title);
      return {
        id: String(p?.entity_id || `${appId}-post-${idx}`),
        backendId: String(p?.entity_id || ''),
        source: 'provisioning' as const,
        archiveSource: 'post' as const,
        timestamp: createdAt || '',
        message: content,
        tags,
        author_key_id: p?.metadata?.source || p?.metadata?.created_by || null,
        title,
        unread,
        status,
        statusLabel: getStatusLabel(status),
        preview: buildPreview('provisioning', status, content, title),
        archived_at: p?.archived_at || null,
      };
    });

    const noteEntries: MailboxEntry[] = notes.map((n: any, idx: number) => {
      const ts = n?.timestamp;
      const unread = toMs(ts) > lastReadMs;
      const tags = Array.isArray(n?.tags) ? n.tags.map(String) : [];
      const title = n?.tool_name || null;
      const message = typeof n?.message === 'string' ? n.message : '';
      const status = inferStatus('cursor-session', tags, message, title);
      return {
        id: String(n?._id || `${appId}-note-${ts || idx}-${idx}`),
        backendId: String(n?._id || ''),
        source: 'cursor-session' as const,
        archiveSource: 'note' as const,
        timestamp: ts || '',
        message,
        tags,
        author_key_id: n?.author_key_id || null,
        title,
        unread,
        status,
        statusLabel: getStatusLabel(status),
        preview: buildPreview('cursor-session', status, message, title),
        archived_at: n?.archived_at || null,
      };
    });

    const merged = [...postEntries, ...noteEntries];
    merged.sort((a, b) => toMs(b.timestamp) - toMs(a.timestamp));
    return merged;
  }, [postsQ.data, notesQ.data, lastReadMs, appId]);

  const activeEntries = useMemo(() => entries.filter((e) => !e.archived_at), [entries]);
  const resolvedEntries = useMemo(() => entries.filter((e) => Boolean(e.archived_at)), [entries]);

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleArchive(entry: MailboxEntry) {
    if (!entry.backendId || archivingIds.has(entry.id)) return;
    setArchivingIds((prev) => new Set(prev).add(entry.id));
    try {
      await archiveMailboxItem(appId, entry.backendId, entry.archiveSource);
      queryClient.invalidateQueries({ queryKey: ['apps', appId, 'handoff-posts'] });
      queryClient.invalidateQueries({ queryKey: ['apps', appId, 'handoff-notes'] });
      queryClient.invalidateQueries({ queryKey: ['mailbox-summary'] });
      queryClient.invalidateQueries({ queryKey: ['overview-mailbox-summary'] });
    } catch (err: any) {
      console.error('[MailboxPanel] archive failed:', err?.message);
    } finally {
      setArchivingIds((prev) => { const next = new Set(prev); next.delete(entry.id); return next; });
    }
  }

  async function handleRestore(entry: MailboxEntry) {
    if (!entry.backendId || archivingIds.has(entry.id)) return;
    setArchivingIds((prev) => new Set(prev).add(entry.id));
    try {
      await unarchiveMailboxItem(appId, entry.backendId, entry.archiveSource);
      queryClient.invalidateQueries({ queryKey: ['apps', appId, 'handoff-posts'] });
      queryClient.invalidateQueries({ queryKey: ['apps', appId, 'handoff-notes'] });
      queryClient.invalidateQueries({ queryKey: ['mailbox-summary'] });
      queryClient.invalidateQueries({ queryKey: ['overview-mailbox-summary'] });
    } catch (err: any) {
      console.error('[MailboxPanel] unarchive failed:', err?.message);
    } finally {
      setArchivingIds((prev) => { const next = new Set(prev); next.delete(entry.id); return next; });
    }
  }

  const isLoading = postsQ.isLoading || notesQ.isLoading;
  const isError = postsQ.isError || notesQ.isError;
  const errorMsg = (notesQ.error as any)?.message || (postsQ.error as any)?.message || 'Failed to load mailbox';

  return (
    <Card className="shadow-sm border border-gray-200">
      <CardContent className="p-6 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <h3 className="font-semibold text-gray-900 truncate">Mailbox</h3>
            {unreadCount > 0 && (
              <span className="inline-flex h-5 px-2 items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-bold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { void postsQ.refetch(); void notesQ.refetch(); }}
              disabled={isLoading}
            >
              <RefreshCw className="w-4 h-4" />
              <span className="ml-1">Refresh</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => void markAllRead()} disabled={unreadCount === 0 || isLoading}>
              Mark all read
            </Button>
          </div>
        </div>

        {isLoading && <div className="text-sm text-gray-500">Loading mailbox…</div>}
        {isError && <div className="text-sm text-red-600">{errorMsg}</div>}

        {!isLoading && !isError && entries.length === 0 && (
          <div className="text-sm text-gray-500">No mailbox items found yet.</div>
        )}

        {!isLoading && !isError && activeEntries.length === 0 && resolvedEntries.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="text-sm text-emerald-800">All mailbox items have been resolved.</span>
          </div>
        )}

        {!isLoading && !isError && activeEntries.length > 0 && (
          <div className="max-h-96 overflow-y-auto space-y-3 pr-1">
            {activeEntries.map((e) => (
              <MailboxItem
                key={e.id}
                entry={e}
                expanded={expandedIds.has(e.id)}
                onToggleExpand={() => toggleExpanded(e.id)}
                onArchive={() => handleArchive(e)}
                onRestore={() => handleRestore(e)}
                archiving={archivingIds.has(e.id)}
              />
            ))}
          </div>
        )}

        {!isLoading && !isError && resolvedEntries.length > 0 && (
          <div className="mt-2 border-t border-slate-200 pt-3">
            <button
              onClick={() => setShowResolved((prev) => !prev)}
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors w-full"
            >
              <Archive className="w-4 h-4" />
              <span>{resolvedEntries.length} resolved</span>
              {showResolved ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
            </button>

            {showResolved && (
              <div className="mt-3 max-h-64 overflow-y-auto space-y-2">
                {resolvedEntries.map((e) => (
                  <MailboxItem
                    key={e.id}
                    entry={e}
                    expanded={expandedIds.has(e.id)}
                    onToggleExpand={() => toggleExpanded(e.id)}
                    onArchive={() => handleArchive(e)}
                    onRestore={() => handleRestore(e)}
                    archiving={archivingIds.has(e.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
